import { filter } from 'lodash';
import { useEffect, useState, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
// material
import {
  Card,
  Table,
  Stack,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Chip,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
// contexts
import { useAuth } from '../../contexts/Auth';
import { SettingsContext } from '../../contexts/Settings';
// helpers
import { getTimeZoneDate, getTimeZoneGTM } from '../../helpers/Util';
// services
import { getOrders } from '../../services/Report';
// components
import Page from '../../components/Page';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { OrderListHead, OrderListToolbar } from '../../components/_dashboard/order';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'number', label: '# Order Number', alignRight: false },
  { id: 'date', label: 'Order Date', alignRight: false, width: 250 },
  { id: 'country', label: 'Country', alignRight: false, width: 90 },
  { id: 'tickets', label: 'Tickets', alignRight: false },
  { id: 'payment_status', label: 'Payment Status', alignRight: false, width: 170 },
  { id: 'payment_method', label: 'Payment Method', alignRight: false, width: 160 },
  { id: 'amount', label: 'Total Amount', alignRight: false, width: 140 }
];

// ----------------------------------------------------------------------

function descendingComparator(a, b) {
  if (b.transaction.order.createdAt < a.transaction.order.createdAt) {
    return -1;
  }
  if (b.transaction.order.createdAt > a.transaction.order.createdAt) {
    return 1;
  }
  return 0;
}

function getComparator(sort) {
  return sort === 'desc'
    ? (a, b) => descendingComparator(a, b)
    : (a, b) => -descendingComparator(a, b);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const sort = comparator(a[0], b[0]);
    if (sort !== 0) return sort;
    return a[1] - b[1];
  });

  if (query) {
    return filter(
      array,
      (_event) => _event.transaction.order.number.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function OrderList() {
  const [searchParams] = useSearchParams();

  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState('desc');
  const [sortBy, setSortBy] = useState('date');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  const { authUser } = useAuth();
  const { authSettings } = useContext(SettingsContext);

  const handleRequestSort = (event, property) => {
    const isAsc = sortBy === property && sort === 'asc';
    setSort(isAsc ? 'desc' : 'asc');
    setSortBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - orders.length) : 0;

  useEffect(() => {
    const fetchData = async (eventId) => {
      setIsLoading(true);

      try {
        let eventDate = null;
        let start = null;
        let end = null;

        const queryEventDate = searchParams.get('eventDate');
        const queryDateStart = searchParams.get('start');
        const queryDateEnd = searchParams.get('end');

        if (queryEventDate) {
          eventDate = getTimeZoneDate({ date: queryEventDate }).toISOString();
        }
        if (queryDateStart) {
          start = getTimeZoneDate({ date: queryDateStart }).toISOString();
        }
        if (queryDateEnd) {
          end = getTimeZoneDate({ date: queryDateEnd }).toISOString();
        }

        const idToken = await authUser.getIdToken();
        const headers = {
          Authorization: `Bearer ${idToken}`,
          'Account-Key': authSettings.server.path
        };

        const orders = await getOrders({ headers, eventId, eventDate, start, end });

        setOrders(orders);
        setIsLoading(false);
      } catch (error) {
        setHasError(true);
        console.error(error);
      }
    };

    const eventId = searchParams.get('eventId');
    if (eventId) {
      fetchData(eventId);
    }
  }, [searchParams, authUser, authSettings, setOrders]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'AUTHORIZED':
        return 'success';
      case 'PENDING':
        return 'warning';
      default:
        return 'error';
    }
  };

  const handleClickOrderRow = (orderId) => {
    window.location.href = `/order/${orderId}?eventId=${searchParams.get('eventId')}`;
  };

  const filteredOrders = applySortFilter(orders, getComparator(sort), filterName);

  const isOrderNotFound = filteredOrders.length === 0;

  if (hasError) {
    return (
      <Container>
        <Alert severity="error">
          An error occurred while trying to load the orders — Please, try to refresh the page!
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  return (
    <Page title="Event | Voy">
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4">Orders</Typography>
        </Stack>

        <Card>
          <OrderListToolbar filterName={filterName} onFilterName={handleFilterByName} />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1060 }}>
              <Table>
                <OrderListHead
                  sort={sort}
                  sortBy={sortBy}
                  headLabel={TABLE_HEAD}
                  rowCount={orders.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredOrders
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row) => {
                      const { _id } = row;

                      return (
                        <TableRow
                          hover
                          key={_id}
                          tabIndex={-1}
                          onClick={() => handleClickOrderRow(row.transaction.order._id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell component="th" scope="row">
                            {row.transaction.order.number}
                          </TableCell>
                          <TableCell>
                            {`${getTimeZoneDate({ date: row.transaction.order.createdAt })
                              .tz('Europe/Lisbon')
                              .format('ddd, DD MMM, HH:mm')}
                              (GTM${getTimeZoneGTM({ date: row.transaction.order.createdAt })})`}
                          </TableCell>
                          <TableCell>{row.transaction.location?.country?.code || '-'}</TableCell>
                          <TableCell align="center">
                            {row.transaction.order.bookings.length}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.transaction.status}
                              color={getStatusColor(row.transaction.status)}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>{row.transaction.payment.method || '-'}</TableCell>
                          <TableCell align="right">{`${row.transaction.amount.total} €`}</TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
                {isOrderNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={7} sx={{ py: 3 }}>
                        <SearchNotFound searchQuery={filterName} />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[25, 100, 250]}
            component="div"
            count={orders.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      </Container>
    </Page>
  );
}
